/**
 * mascots — the tutor mascot registry. ONE definition, shared by the browser and
 * the ai-tutor function, so a mascot's art and its NAME can never disagree.
 *
 * Pure data: no firebase, no netlify, no process.env, no React. That is what lets
 * `src/` import it across the netlify boundary (same discipline as tutorPrompt.ts).
 *
 * A mascot is a look AND a persona. The name here is the name the model is told to
 * answer to, the name on the panel header, and the name in the greeting. They must
 * be the same string or the illusion breaks the moment a student reads the reply.
 *
 * ── Adding a mascot ────────────────────────────────────────────────────────────
 * 1. Export the sprite sheet as ONE image on a UNIFORM grid, transparent
 *    background, poses in reading order (see MASCOT_POSES). Every cell must be the
 *    same size and the art must sit INSIDE its cell — a prop that overhangs (an
 *    easel, a wing) gets clipped by the neighbouring cell, because the sprite is a
 *    background crop, not a cutout.
 * 2. Drop it at `public/mascots/<id>-sheet.png`.
 * 3. Add an entry below.
 * The owner dashboard picks it up automatically; no other file needs touching.
 */

/**
 * The 12 poses, in sheet reading order (left→right, top→bottom). The grid position
 * is DERIVED from this order, so a sheet is described by its dimensions alone.
 */
export const MASCOT_POSES = [
  'wave', 'idle', 'thinking', 'point',
  'ponder', 'thumbsUp', 'hi', 'reading',
  'listening', 'presenting', 'celebrate', 'waveAlt',
] as const;

export type MascotPose = (typeof MASCOT_POSES)[number];

export interface MascotDef {
  /** Stable id. Stored on enterpriseDomains.tutorMascot; never rename one in place. */
  id: string;
  /** The tutor's name. Goes into the system prompt verbatim. */
  name: string;
  /** What it is, for the persona line: "a warm ... newt". Keep it to a word or two. */
  species: string;
  /** Sheet path under public/. */
  sheet: string;
  cols: number;
  rows: number;
  /** Panel header gradient, drawn from the character's own palette. */
  gradient: string;
  /** Shown next to the name in the owner picker. Never seen by a student. */
  blurb: string;
}

/**
 * Registry. Keys are the stored ids.
 *
 * `satisfies` (not `:`) so each entry keeps its literal type — MASCOT_IDS stays a
 * union of the real ids rather than widening to string.
 */
export const MASCOTS = {
  newt: {
    id: 'newt',
    name: 'Newt',
    species: 'gecko',
    sheet: '/mascots/newt-sheet.png',
    cols: 4,
    rows: 3,
    gradient: 'linear-gradient(135deg,#59A8DA,#5FA660)',
    blurb: 'The Level Up house study buddy. Green, wry, everywhere by default.',
  },
  quill: {
    id: 'quill',
    name: 'Quill',
    species: 'owl',
    sheet: '/mascots/quill-sheet.png',
    cols: 4,
    rows: 3,
    gradient: 'linear-gradient(135deg,#1D3FA5,#E8A81C)',
    blurb: 'Blue-and-gold graduate owl. Built for Ontario Education Online.',
  },
} satisfies Record<string, MascotDef>;

export type MascotId = keyof typeof MASCOTS;

/** The mascot every non-enterprise user gets, and the fallback for an unknown id. */
export const DEFAULT_MASCOT_ID: MascotId = 'newt';

export const MASCOT_IDS = Object.keys(MASCOTS) as MascotId[];

export const isMascotId = (id: unknown): id is MascotId =>
  typeof id === 'string' && Object.prototype.hasOwnProperty.call(MASCOTS, id);

/**
 * A stored id → its definition, ALWAYS. An unknown id (a mascot removed from the
 * registry while an org still points at it) falls back rather than throwing: a
 * retired character must not take a school's tutor offline.
 *
 * This is also the injection boundary. The persona reaching the system prompt is
 * whatever this returns, so it can only ever be a string the registry owns — a
 * client that posts `{ mascotId: "ignore previous instructions" }` gets Newt.
 */
export function resolveMascot(id: unknown): MascotDef {
  return isMascotId(id) ? MASCOTS[id] : MASCOTS[DEFAULT_MASCOT_ID];
}

/** [col, row] of a pose on its sheet, derived from reading order. */
export function poseCell(mascot: MascotDef, pose: MascotPose): [number, number] {
  const i = MASCOT_POSES.indexOf(pose);
  const idx = i < 0 ? 0 : i;
  return [idx % mascot.cols, Math.floor(idx / mascot.cols)];
}
